import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { useGetInboxesWithCustom } from '@/api/chat'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import TextField from '../../FormLegacy/FormFieldsV2/TextField'
import Icon from '../../Icons'
import { getAssetLink } from '../../../helpers/get-asset-link'
import { getDate, getProperTeamNameByType, conversationStatusMap } from '../helpers'

import InboxSelector from './InboxSelector'
import AudioMessage from './AudioMessage'

const ChatSidebar = ({
  conversations,
  createChat,
  selectChat,
  selectedChat,
  selectedChannelFilter,
  unreadConversationIds,
  closedConversationsData,
  assignedConversationData,
  isLoadingConversations,
  currentUserData,
  selectChannel,
  search,
  setSearch,
  conversationAttributeFilter,
}) => {
  const { t } = useTranslation(['chat'])
  const [filteredConversations, setFilteredConversations] = useState([])

  const currentBuilding = useSelector(selectActiveBuilding)
  const { inboxMappings } = useGetInboxesWithCustom({
    buildingId: currentBuilding?.id,
    chatCustom: currentBuilding?.chatCustom,
  })

  const isUnread = (conversation) => {
    return selectedChat
      ? selectedChat.channelName !== conversation.channelName &&
          unreadConversationIds.includes(conversation.channelName)
      : unreadConversationIds.includes(conversation.channelName)
  }

  const onChatClick = (clickedChat) => {
    if (selectedChat && selectedChat.channelName === clickedChat.channelName) return
    selectChat(clickedChat)
  }

  const renderDateContent = (conversation) => {
    if (conversation.lastMessage) {
      return getDate(conversation.updatedAt)
    }
    return ''
  }

  useEffect(() => {
    if (!conversations) return

    let filteredConv = conversations

    if (search) {
      filteredConv = filteredConv.filter((conversation) =>
        conversation.name.toLowerCase().includes(search.toLowerCase()),
      )
    }

    switch (conversationAttributeFilter) {
      case 'open':
        filteredConv = filteredConv.filter((c) => c.status === conversationStatusMap.OPEN)
        break
      case 'closed':
        filteredConv = filteredConv.filter((c) => c.status === conversationStatusMap.CLOSED)
        break
      case 'archived':
        filteredConv = filteredConv.filter((c) => c.status === conversationStatusMap.ARCHIVED)
        break
      case 'assignedToYou':
        filteredConv = filteredConv.filter((conversation) =>
          assignedConversationData.some(
            (assigned) =>
              assigned.assignee === currentUserData.user.id &&
              assigned.name === conversation.channelName,
          ),
        )
        break
      case 'unassigned':
        filteredConv = filteredConv.filter(
          (conversation) =>
            !assignedConversationData
              .map((assigned) => assigned.name)
              .includes(conversation.channelName),
        )
        break
      default:
        break
    }

    setFilteredConversations(filteredConv)
  }, [
    conversations,
    conversationAttributeFilter,
    closedConversationsData,
    assignedConversationData,
    currentUserData,
    search,
    unreadConversationIds,
  ])

  const currentType = selectedChannelFilter?.type || 'ALL'

  return (
    <div className="chat__sidebar">
      <InboxSelector
        value={selectedChannelFilter}
        onChange={selectChannel}
        disabled={isLoadingConversations}
      />
      {!['MT', 'CUST', 'ALL'].includes(currentType) && (
        <div className={`new-chat-button-container ${isLoadingConversations ? 'loading' : ''}`}>
          <div className="new-chat-button" onClick={() => createChat(true)}>
            {t('chat:chatSidebar.startNewChat')}
          </div>
        </div>
      )}
      <div className="search-channels-input">
        <TextField
          disabled={isLoadingConversations}
          maxLength="50"
          name={'filter'}
          placeholder={t('chat:chatSidebar.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && (
          <span onClick={() => setSearch('')}>
            <Icon icon="cross" />
          </span>
        )}
      </div>

      <div className={`channels ${isLoadingConversations ? 'is-loading' : ''}`}>
        {filteredConversations.map((conversation) => (
          <div
            className={`channel-container ${isUnread(conversation) && 'is-unread'}`}
            key={conversation.id}
            onClick={() => onChatClick(conversation)}
          >
            <div className="channel-header">
              <div className="avatar-container">
                <img
                  src={getAssetLink({ path: 'chitown-avatar/avatar', file: conversation.avatar })}
                  alt=""
                />
              </div>
              <div className="channel-info">
                <div className="channel-row channel-row--name-date">
                  <div className="name">{conversation.name}</div>
                  <div className="date">{renderDateContent(conversation)}</div>
                </div>
                <div className="channel-row">
                  <div className="user-number">{conversation.phone}</div>
                  <div className="conversation-team">
                    {conversation.teamType === 'CUST'
                      ? inboxMappings.get(conversation.channelName.split('_')[2])?.name ||
                        getProperTeamNameByType(conversation.teamType)
                      : getProperTeamNameByType(conversation.teamType)}
                  </div>
                </div>
              </div>
            </div>
            <div className="channel-footer">
              <div className="footer-left">
                {conversation.lastMessage && (
                  <>
                    {conversation.lastMessageStatus ? (
                      <AudioMessage
                        text={
                          conversation.emergency
                            ? t('chat:audioMessage.emergencyVoicemail')
                            : conversation.lastMessage
                        }
                        status={
                          conversation.emergency
                            ? 'Emergency Voicemail'
                            : conversation.lastMessageStatus
                        }
                        isPreview
                      />
                    ) : (
                      <div className="last-message">
                        <div className="last-message-avatar">
                          <img src={conversation.lastMessageAvatar} alt="" />
                        </div>
                        <span className="colon">:</span>
                        <div className="last-message-text">{conversation.lastMessage}</div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="footer-right">
                <div className="message-type">CHAT</div>
                {isUnread(conversation) && <div className="notification-dot" />}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

ChatSidebar.propTypes = {
  conversations: PropTypes.array,
  createChat: PropTypes.func,
  selectChat: PropTypes.func,
  selectedChat: PropTypes.object,
  selectedChannelFilter: PropTypes.string,
  unreadConversationIds: PropTypes.arrayOf(PropTypes.string),
  closedConversationsData: PropTypes.arrayOf(PropTypes.object),
  assignedConversationData: PropTypes.arrayOf(PropTypes.object),
  isLoadingConversations: PropTypes.bool,
  currentUserData: PropTypes.object,
  selectChannel: PropTypes.func,
  search: PropTypes.string,
  setSearch: PropTypes.func,
  conversationAttributeFilter: PropTypes.string,
}

export default ChatSidebar
